import { useState } from 'react'

import { Tabs, Button, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { MAX_UPLOADED_FILES_PER_SHIPPER } from '../../../domain/Document'
import { type ShipperDetail } from '../../../domain/shippers'
import { useDeleteShipperAttachment } from '../../../hooks/useDeleteShipperAttachment'
import { useShipperAttachments } from '../../../hooks/useShipperAttachments'
import { DocumentListItem } from './DocumentListItem'
import { UploadDocumentDialog } from './UploadDocumentDialog'

export type ShipperDocumentsTabProps = {
  details: ShipperDetail
}

export function ShipperDocumentsTab({ details }: ShipperDocumentsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  const { carrierDocuments, shipperDocuments, refetch, status } = useShipperAttachments(
    details.uuid
  )
  const { deleteAttachment } = useDeleteShipperAttachment({ onSuccess: refetch })
  const isUploadDisabled = carrierDocuments.length >= MAX_UPLOADED_FILES_PER_SHIPPER

  const isFetched = status === RequestStatus.Done
  const hasAnyDocuments = carrierDocuments.length > 0 || shipperDocuments.length > 0
  const showEmptyState = isFetched && !hasAnyDocuments

  function openDialog() {
    setDialogOpen(true)
  }
  function closeDialog() {
    setDialogOpen(false)
  }

  return (
    <>
      <Tabs.Tab name="documents">Documents</Tabs.Tab>
      <Tabs.Panel forTab="documents">
        <Layout.Stack gap="spacing-4">
          {showEmptyState ? (
            <Layout.Group align="center" justify="center" style={{ height: '124px' }}>
              <Text variant="body-md" color="color-text-disabled">
                No documents
              </Text>
            </Layout.Group>
          ) : (
            <>
              {carrierDocuments.length > 0 && (
                <Layout.Stack gap="spacing-2">
                  <Text variant="body-sm-bold" color="color-neutral-60">
                    MY DOCUMENTS
                  </Text>
                  <Layout.Stack gap="spacing-2">
                    {carrierDocuments.map(attachment => (
                      <DocumentListItem
                        key={attachment.id}
                        attachment={attachment}
                        shipperUuid={details.uuid}
                        onDelete={() =>
                          deleteAttachment({
                            shipperUuid: details.uuid,
                            attachmentId: attachment.id,
                          })
                        }
                      />
                    ))}
                  </Layout.Stack>
                </Layout.Stack>
              )}

              {shipperDocuments.length > 0 && (
                <Layout.Stack gap="spacing-2">
                  <Text variant="body-sm-bold" color="color-neutral-60">
                    SHIPPER DOCUMENTS
                  </Text>
                  <Layout.Stack gap="spacing-2">
                    {shipperDocuments.map(attachment => (
                      <DocumentListItem
                        key={attachment.id}
                        attachment={attachment}
                        shipperUuid={details.uuid}
                      />
                    ))}
                  </Layout.Stack>
                </Layout.Stack>
              )}
            </>
          )}

          <Button
            variant="secondary"
            type="button"
            onClick={openDialog}
            style={{ width: 'fit-content' }}
            disabled={isUploadDisabled}
          >
            <Icon
              name="upload"
              color={isUploadDisabled ? 'color-text-disabled' : 'color-primary-100'}
            />
            Upload documents
          </Button>
        </Layout.Stack>
      </Tabs.Panel>
      <UploadDocumentDialog
        open={dialogOpen}
        onClose={closeDialog}
        onUploadSuccess={refetch}
        shipperUUID={details.uuid}
      />
    </>
  )
}
